<template>
  <div class="search-button base-button">
    <input @change="onClickOpen" ref="searchInputCheckbox" type="checkbox" :checked="isFilter" :id="searchButtonId" class="search-button-checkbox" />
    <label ref="openSearchButton" class="open-search-input-button-container" :for="searchButtonId">
      <div class="open-search-input-button">
        <search-icon />
      </div>
    </label>
    <div class="input-group hidden" ref="searchInputGroup">
      <search-icon class="input-search-icon" />
      <input
        :disabled="false"
        ref="searchInput"
        type="text"
        :value="modelValue"
        :placeholder="t('component.button.search.placeholder')"
        @input="updateValue"
        class="search-input"
        :aria-label="t('component.button.search.placeholder')"
      />
      <button class="input-close-button" type="button" @click="onClickCloseButton">
        <close-icon />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// import SearchIcon from '@/assets/icons/base/button/SearchIcon.vue';
import SearchIcon from '@/assets/icons/base/search-md.vue';

import CloseIcon from '@/assets/icons/base/button/CloseIcon.vue';

import { ref, Ref, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  searchButtonId?: string;
  tooltipLabel?: string;
  disabled?: boolean;
  isFilter?: boolean;
  modelValue?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  searchButtonId: 'search-button-checkbox',
  tooltipLabel: '',
  disabled: false,
  isFilter: false,
});

const emit = defineEmits(['update:modelValue', 'input', 'toggle-search-button']);

const searchInput: Ref<HTMLInputElement> = ref(null!);
const searchInputCheckbox: Ref<HTMLInputElement> = ref(null!);
const searchInputGroup: Ref<HTMLDivElement> = ref(null!);
const openSearchButton: Ref<HTMLDivElement> = ref(null!);
const { t } = useI18n();

const updateValue = (event: any) => {
  emit('update:modelValue', event.target.value);
  emit('input', event.target.value);
};

const onClickCloseButton = () => {
  emit('toggle-search-button', false);
  if (!props.isFilter) {
    searchInputCheckbox.value.checked = false;
    searchInputGroup.value.classList.remove('active');
    searchInputGroup.value.classList.add('hidden');
    openSearchButton.value.classList.remove('hide');
  }

  searchInput.value.value = '';

  if (props.modelValue !== searchInput.value.value) {
    emit('update:modelValue', searchInput.value.value);
    emit('input', searchInput.value.value);
  }
};

const onClickOpen = (event: Event) => {
  emit('toggle-search-button', true);
  const checkbox = event.target as HTMLInputElement;

  if (checkbox.checked) {
    searchInputGroup.value.classList.remove('hidden');
    searchInputGroup.value.classList.add('active');
    openSearchButton.value.classList.add('hide');
    return searchInput.value.focus();
  }

  searchInputGroup.value.classList.remove('active');
  searchInputGroup.value.classList.add('hidden');
  openSearchButton.value.classList.remove('hide');
};

onMounted(() => {
  if (props.isFilter || !!props.modelValue) {
    searchInputGroup.value.classList.remove('hidden');
    searchInputGroup.value.classList.add('active');
    openSearchButton.value.classList.add('hide');
  }
});

watch(
  () => props.isFilter,
  () => {
    if (props.isFilter) {
      searchInputGroup.value.classList.remove('hidden');
      searchInputGroup.value.classList.add('active');
      return openSearchButton.value.classList.add('hide');
    }

    onClickCloseButton();
  },
);

watch(
  () => props.disabled,
  () => {
    if (!props.disabled) {
      nextTick(() => {
        searchInput.value.focus();
      });
    }
  },
);
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/button.scss';
</style>
